import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DataTable from '../components/table/DataTable';
import { TablePagination } from '../components/table/SortableHeader';
import {
    ArrowLeft, Search, Loader2, Download, RefreshCw, Inbox,
    Calendar, TrendingUp, Clock, Mail, ExternalLink, Trash2
} from 'lucide-react';
import { getAllFieldsFromSteps, flattenInputFields } from '../lib/formUtils';
import { isOrgAdminUser } from '../utils/orgRoles';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Build column definitions from a form's field definitions.
 *
 * Derives columns from ALL question steps (not just the first), recursively
 * flattening containers and filtering to input-only fields. Falls back to
 * form.fields (legacy mirror) only when there are no steps.
 */
function buildColumns(form) {
    // Primary source: all question steps (non-router), deduped by field_id
    let inputFields;
    if (form.steps && form.steps.length > 0) {
        inputFields = getAllFieldsFromSteps(form.steps);
    } else {
        // Legacy fallback: form.fields mirror
        inputFields = flattenInputFields(form.fields || []);
    }

    // Build column defs for DataTable
    return inputFields.map(f => ({
        key: f.field_id,
        label: f.label,
        type: f.field_type === 'email' ? 'email' : f.field_type === 'number' ? 'number' : 'text',
        width: 150,
    }));
}

/**
 * Extract a displayable value for a field from a submission's answers object.
 * Falls back to '-' when absent.
 */
function getFieldValue(submission, fieldId) {
    const answers = submission.answers || submission.data || {};
    const val = answers[fieldId];
    if (val == null || val === '') return '—';
    if (Array.isArray(val)) return val.join(', ');
    return val;
}

const STATUS_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
];

export default function FormDataPage() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader, user } = useAuth();

    // Form metadata + column definitions
    const [form, setForm] = useState(null);
    const [fieldColumns, setFieldColumns] = useState([]);

    // Submissions (server-side paginated)
    const [submissions, setSubmissions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);

    // Search (server-side, debounced)
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const debounceRef = useRef(null);

    // Sorting (server-side)
    const [sorting, setSorting] = useState([]);

    // Status filter (client-side on loaded page)
    const [statusFilter, setStatusFilter] = useState('');

    // Detail modal
    const [selectedSubmission, setSelectedSubmission] = useState(null);

    // Owner-gated submission delete (#562)
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [pendingDelete, setPendingDelete] = useState(null); // { ids: [...] }
    const [deleting, setDeleting] = useState(false);

    // ─── Load form metadata ──────────────────────────────────────────────
    const loadForm = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() });
            setForm(res.data);
            setFieldColumns(buildColumns(res.data));
        } catch (err) {
            console.error('Load form error:', err);
            toast.error('Failed to load form');
        }
    }, [formId, getAuthHeader]);

    // ─── Load submissions (server-side paginated/searchable/sortable) ────
    const loadSubmissions = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: pageSize,
            };
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (sorting[0]) {
                params.sort_by = sorting[0].id;
                params.sort_dir = sorting[0].desc ? 'desc' : 'asc';
            }
            const res = await axios.get(`${API}/forms/${formId}/submissions`, {
                params,
                headers: getAuthHeader(),
            });
            // API returns { data, total, page, page_size }
            const payload = res.data || {};
            setSubmissions(Array.isArray(payload) ? payload : (payload.data || []));
            setTotal(Array.isArray(payload) ? payload.length : (payload.total || 0));
        } catch (err) {
            console.error('Load submissions error:', err);
            if (err?.response?.status === 401) {
                toast.error('Session expired — please refresh the page');
            } else {
                toast.error('Failed to load submissions');
            }
            setSubmissions([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [formId, page, pageSize, search, statusFilter, sorting, getAuthHeader]);

    // Initial load of form metadata
    useEffect(() => {
        loadForm();
    }, [loadForm]);

    // Reload submissions when pagination/search/sort params change
    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    // ─── Debounced search ────────────────────────────────────────────────
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
            setSelectedIds(new Set()); // page-scoped selection (#562)
        }, 400);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchInput]);

    // ─── Sorting handler ─────────────────────────────────────────────────
    const handleSortingChange = useCallback((updater) => {
        setSorting(updater);
        setPage(1);
        setSelectedIds(new Set()); // page-scoped selection (#562)
    }, []);

    // ─── Page size handler ───────────────────────────────────────────────
    const handlePageSizeChange = useCallback((size) => {
        setPageSize(size);
        setPage(1);
        setSelectedIds(new Set()); // page-scoped selection (#562)
    }, []);

    // ─── CSV export ──────────────────────────────────────────────────────
    const exportCsv = async () => {
        try {
            const res = await axios.get(`${API}/forms/${formId}/submissions/export`, {
                headers: getAuthHeader(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `form-submissions-${formId}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('CSV exported');
        } catch (err) {
            console.error('CSV export failed:', err);
            toast.error('Export failed');
        }
    };

    // ─── Refresh ─────────────────────────────────────────────────────────
    const handleRefresh = () => {
        loadForm();
        loadSubmissions();
    };

    // ─── Owner-gated submission delete (#562) ────────────────────────────
    const deleteSubmissions = useCallback(async (ids) => {
        if (!ids || ids.length === 0) return;
        setDeleting(true);
        try {
            let removed;
            if (ids.length > 1) {
                const res = await axios.post(
                    `${API}/forms/${formId}/submissions/bulk-delete`,
                    { submission_ids: ids },
                    { headers: getAuthHeader() },
                );
                const data = res.data || {};
                removed = data.deleted ?? 0;
                const notFound = data.not_found || [];
                if (notFound.length > 0) {
                    const requested = data.requested ?? ids.length; // echo the server's deduped contract
                    toast.warning(`Deleted ${removed} of ${requested} submissions — ${notFound.length} no longer exist`);
                } else {
                    toast.success(`Deleted ${removed} ${removed === 1 ? 'submission' : 'submissions'}`);
                }
            } else {
                await axios.delete(`${API}/forms/${formId}/submissions/${ids[0]}`, {
                    headers: getAuthHeader(),
                });
                toast.success('Submission deleted');
                removed = 1;
            }
            setPendingDelete(null);
            setSelectedIds(new Set());
            // Reload; step back a page when the current page just went empty.
            if (page > 1 && submissions.length - removed <= 0) {
                setPage(page - 1);
            } else {
                loadSubmissions();
            }
        } catch (err) {
            console.error('Delete submissions failed:', err);
            const status = err?.response?.status;
            if (status === 403) toast.error('Only the form owner can delete submissions');
            else if (status === 404) toast.error('Submission not found — it may have been deleted already');
            else toast.error('Failed to delete submissions');
        } finally {
            setDeleting(false);
        }
    }, [formId, getAuthHeader, loadSubmissions, page, submissions]);

    // Row-level delete affordance, passed to DataTable as renderActions.
    // stopPropagation keeps the row-click (detail dialog) from firing too.
    const renderRowAction = useCallback((row) => (
        <button
            data-testid={`delete-submission-${row.submission_id}`}
            onClick={(e) => {
                e.stopPropagation();
                setPendingDelete({ ids: [row.submission_id] });
            }}
            className="text-stone-400 hover:text-red-600 p-1 rounded transition-colors"
            title="Delete submission"
        >
            <Trash2 className="w-4 h-4" />
        </button>
    ), []);

    // ─── Client-side status filter on loaded page ────────────────────────
    const filteredSubmissions = useMemo(() => {
        // Flatten answers onto each row's top level so DataTable's
        // accessorFn (row[col.key] === row[field_id]) resolves correctly.
        // Without this, answers stay nested at row.answers[field_id] and
        // the table shows "—" for every field column.
        // NOTE: Status filtering is now server-side via the `status` query param.
        const flatten = (s) => ({ ...s, ...(s.answers || s.data || {}) });
        return submissions.map(flatten);
    }, [submissions]);

    // ─── Build visible columns for DataTable ─────────────────────────────
    // First 5 field columns + metadata columns (Submitted At, Status, Contact)
    const visibleColumns = useMemo(() => {
        const cols = [...fieldColumns.slice(0, 5)];
        cols.push({ key: 'created_at', label: 'Submitted At', type: 'date', width: 160 });
        cols.push({ key: 'status', label: 'Status', type: 'badge', width: 120 });
            return cols;
    }, [fieldColumns]);

    // ─── KPI stats ────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);

        const thisWeek = submissions.filter(s => {
            const d = new Date(s.created_at);
            return !isNaN(d.getTime()) && d >= weekAgo;
        }).length;

        // Conversion rate: submissions that linked to a contact
        const linked = submissions.filter(s => s.contact_id).length;
        const conversionRate = total > 0 ? Math.round((linked / total) * 100) : 0;

        // Avg completion: percentage of required fields filled (best-effort)
        const requiredFields = fieldColumns.length;
        let avgCompletion = 0;
        if (requiredFields > 0 && submissions.length > 0) {
            const completionSum = submissions.reduce((sum, s) => {
                const answers = s.answers || s.data || {};
                const filled = fieldColumns.filter(f => {
                    const v = answers[f.key];
                    return v != null && v !== '';
                }).length;
                return sum + (filled / requiredFields);
            }, 0);
            avgCompletion = Math.round((completionSum / submissions.length) * 100);
        }

        return {
            totalEntries: total,
            thisWeek,
            conversionRate,
            avgCompletion,
        };
    }, [submissions, total, fieldColumns]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // ─── Owner gate for destructive actions (#562) ──────────────────────
    // Mirrors the server rule in forms._require_form_deleter: the form
    // owner, an org admin (isOrgAdminUser — the same vocabulary as the
    // backend's rbac.is_org_admin, legacy `admin` alias included), or the
    // instance admin (is_admin). Purely a UI affordance — the server
    // enforces the same rule independently.
    const isOrgAdmin = isOrgAdminUser(user);
    const isOwner = Boolean(form?.created_by) && form?.created_by === user?.user_id;
    const canDelete = isOwner || isOrgAdmin;

    // ─── Loading state ───────────────────────────────────────────────────
    if (loading && !form) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-stone-50">
                <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
            </div>
        );
    }

    // ─── Form not found ─────────────────────────────────────────────────
    if (!form) {
        return (
            <div className="min-h-screen bg-stone-50 p-6">
                <div className="text-center py-20">
                    <Inbox className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400 mb-4">Form not found</p>
                    <Button variant="outline" onClick={() => navigate('/forms')}>
                        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Forms
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            {/* ─── Page Header ───────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-start gap-4">
                    <button
                        onClick={() => navigate('/forms')}
                        className="mt-1 p-2 rounded-lg border border-stone-200 bg-white text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors"
                        title="Back to Forms"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {form.name}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <Inbox className="w-3.5 h-3.5 text-stone-400" />
                            <span className="text-sm text-stone-500">{total} submissions</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportCsv} disabled={total === 0}>
                        <Download className="w-3.5 h-3.5 mr-1" />
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* ─── KPI Stats Row ─────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Inbox className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Total Entries</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{stats.totalEntries.toLocaleString()}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">This Week</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{stats.thisWeek.toLocaleString()}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Conversion Rate</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{stats.conversionRate}%</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Avg Completion</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{stats.avgCompletion}%</span>
                </div>
            </div>

            {/* ─── Toolbar: Search + Status Filter ───────────────────────── */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-3 flex-1 flex-wrap">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <input
                            type="text"
                            placeholder="Search submissions..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value); setPage(1); setSelectedIds(new Set()); }}
                        className="px-2.5 py-1.5 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none"
                    >
                        <option value="">All Status</option>
                        {STATUS_OPTIONS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Selection action bar (#562) — owners/admins only, and only
                while at least one row on this page is selected */}
            {canDelete && selectedIds.size > 0 && (
                <div className="flex items-center justify-between gap-3 bg-white border border-stone-200 rounded-lg px-4 py-2 mb-3">
                    <span className="text-sm font-medium text-stone-600">
                        {selectedIds.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedIds(new Set())}
                            disabled={deleting}
                        >
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                            onClick={() => setPendingDelete({ ids: [...selectedIds] })}
                            disabled={deleting}
                        >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Delete selected
                        </Button>
                    </div>
                </div>
            )}

            {/* ─── Data Table ────────────────────────────────────────────── */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                </div>
            ) : filteredSubmissions.length === 0 ? (
                /* Empty state */
                <div className="bg-white rounded-xl border border-stone-200 text-center py-16">
                    <Inbox className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">
                        {search || statusFilter
                            ? 'No submissions match your filters'
                            : 'No submissions yet. Share your form to start collecting data!'}
                    </p>
                </div>
            ) : (
                <>
                    <DataTable
                        data={filteredSubmissions}
                        visibleColumns={visibleColumns}
                        sorting={sorting}
                        onSortingChange={handleSortingChange}
                        onRowClick={(row) => setSelectedSubmission(row)}
                        idField="submission_id"
                        selectable={canDelete}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        renderActions={canDelete ? renderRowAction : undefined}
                    />
                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        total={total}
                        pageSize={pageSize}
                        onPageChange={(p) => { setSelectedIds(new Set()); setPage(p); }}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[10, 25, 50]}
                        alwaysShow
                    />
                </>
            )}

            {/* ─── Submission Detail Modal ──────────────────────────────── */}
            <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
                    <DialogHeader className="px-6 pt-5 pb-3 shrink-0">
                        <DialogTitle className="flex items-center gap-2 text-stone-900">
                            <Inbox className="w-5 h-5 text-stone-400" />
                            Submission Details
                        </DialogTitle>
                    </DialogHeader>
                    {selectedSubmission && (
                        <div className="flex-1 overflow-y-auto px-6 py-2 space-y-4">
                            {/* Metadata */}
                            <div className="grid grid-cols-2 gap-3 bg-stone-50 rounded-lg p-3">
                                <div>
                                    <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Submitted</span>
                                    <p className="text-sm text-stone-700 mt-0.5">
                                        {selectedSubmission.created_at
                                            ? new Date(selectedSubmission.created_at).toLocaleString()
                                            : '—'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</span>
                                    <p className="text-sm text-stone-700 mt-0.5 capitalize">
                                        {selectedSubmission.status || 'new'}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Contact</span>
                                    <p className="text-sm text-stone-700 mt-0.5">
                                        {selectedSubmission.contact_id ? (
                                            <button
                                                onClick={() => navigate(`/contacts/${selectedSubmission.contact_id}`)}
                                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                            >
                                                Linked <ExternalLink className="w-3 h-3" />
                                            </button>
                                        ) : (
                                            'Not linked'
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Field answers */}
                            <div>
                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                                    Form Responses
                                </h4>
                                <div className="space-y-2">
                                    {fieldColumns.length === 0 ? (
                                        <p className="text-sm text-stone-400">No fields defined for this form.</p>
                                    ) : (
                                        fieldColumns.map(col => {
                                            const value = getFieldValue(selectedSubmission, col.key);
                                            const isEmail = col.type === 'email' && value !== '—';
                                            return (
                                                <div key={col.key} className="flex items-start justify-between gap-4 py-1.5 border-b border-stone-100 last:border-0">
                                                    <span className="text-sm font-medium text-stone-600 flex-shrink-0">{col.label}</span>
                                                    <span className="text-sm text-stone-700 text-right break-words min-w-0 flex items-center gap-1">
                                                        {isEmail ? (
                                                            <>
                                                                <Mail className="w-3 h-3 text-stone-400 flex-shrink-0" />
                                                                <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-700 truncate">
                                                                    {value}
                                                                </a>
                                                            </>
                                                        ) : (
                                                            <span className="truncate">{value}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Forwarder delivery status (per-forwarder results) */}
                            {Array.isArray(selectedSubmission.forwarder_results) && selectedSubmission.forwarder_results.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                                        Forwarder Delivery
                                        {selectedSubmission.forwarder_status && (
                                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                                selectedSubmission.forwarder_status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                                                selectedSubmission.forwarder_status === 'failed' ? 'bg-red-100 text-red-700' :
                                                selectedSubmission.forwarder_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                                'bg-stone-100 text-stone-600'
                                            }`}>
                                                {selectedSubmission.forwarder_status}
                                            </span>
                                        )}
                                    </h4>
                                    <div className="bg-stone-50 rounded-lg p-3 space-y-1.5">
                                        {selectedSubmission.forwarder_results.map(r => (
                                            <div key={r.forwarder_id || r.name} className="flex items-center justify-between gap-2 text-sm">
                                                <span className="text-stone-600 truncate">{r.name || r.type}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                                    r.status === 'success' ? 'bg-emerald-100 text-emerald-700' :
                                                    r.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                    r.status === 'skipped' ? 'bg-stone-100 text-stone-500' :
                                                    'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {r.status}
                                                </span>
                                                {r.latency_ms != null && <span className="text-xs text-stone-400">{r.latency_ms}ms</span>}
                                                {r.error && <span className="text-xs text-red-600 truncate" title={r.error}>{r.error}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {canDelete && selectedSubmission && (
                        <div className="px-6 pb-5 pt-3 border-t border-stone-100 flex justify-end">
                            <Button
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                    setSelectedSubmission(null);
                                    setPendingDelete({ ids: [selectedSubmission.submission_id] });
                                }}
                                disabled={deleting}
                            >
                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                Delete submission
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirmation (#562) — controlled, shared by the row
                action, the selection bar, and the detail dialog */}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(open) => { if (!open && !deleting) setPendingDelete(null); }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Delete {pendingDelete?.ids.length === 1 ? '1 submission' : `${pendingDelete?.ids.length ?? 0} submissions`}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone from the UI. The submission is removed
                            from this form's data and its entry count.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setPendingDelete(null)} disabled={deleting}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 text-white hover:bg-red-700"
                            onClick={(e) => { e.preventDefault(); deleteSubmissions(pendingDelete?.ids || []); }}
                            disabled={deleting || !pendingDelete}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
